import React, { useState, useEffect, useCallback, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Icon,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Tooltip,
} from '@material-ui/core';
import moment from 'moment';

import Actions from '../../../../../../store/ducks/appointments';
import Loading from './Loading';
import useStyles from './styles';

const SlotProfessionalsModal = ({
  onExited,
  onSelect,
  date,
  timeTableId,
  historyId,
  professionalsSlotInfos,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    history: { list: listHistory, allLoaded: historyLoaded },
    professionals: { list: listProfessionals, allLoaded: professionalsLoaded },
    schedule: {
      columns: { timetable },
    },
  } = useSelector(state => state.appointments);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [appointmentInfos, setAppointmentInfos] = useState(null);
  const [timeRange, setTimeRange] = useState([]);

  useEffect(() => {
    if (timeTableId in timetable) {
      const { startTime, endTime } = timetable[timeTableId];
      setTimeRange([startTime, endTime]);
    }
  }, [timeTableId, timetable]);

  useEffect(() => {
    if (historyId && !historyLoaded) {
      dispatch(Actions.requestAppointments());
    }
  }, [dispatch, historyId, historyLoaded]);

  useEffect(() => {
    if (historyId in listHistory) {
      setAppointmentInfos(listHistory[historyId]);
    } else {
      setAppointmentInfos(null);
    }
  }, [historyId, listHistory]);

  useEffect(() => {
    if (!professionalsLoaded) {
      dispatch(Actions.requestProfessionals());
    }
  }, [dispatch, professionalsLoaded]);

  useEffect(() => {
    const appointmentInfosLoaded = historyId ? !!appointmentInfos : true;
    setLoading(!(appointmentInfosLoaded && professionalsLoaded));
  }, [professionalsLoaded, appointmentInfos, historyId]);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSelectProfessional = useCallback(
    (id, meetingId) => {
      onSelect(id, meetingId);
      handleCloseModal();
    },
    [onSelect, handleCloseModal],
  );

  const getTooltipVacancies = useCallback((filled, total) => {
    const available = total - filled;
    if (available < 1) {
      return `Não restam vagas disponíveis para este horário.`;
    }
    if (available === 1) {
      return `Resta 1 vaga disponível para este horário.`;
    }
    return `Restam ${available} vagas disponíveis para este horário.`;
  }, []);

  return (
    <Dialog
      className={classes.container}
      classes={{ paper: classes.paper }}
      onClose={handleCloseModal}
      onExited={onExited}
      open={open}
      maxWidth="xs"
    >
      <DialogContent className="slot-details-content">
        {loading ? (
          <Loading />
        ) : (
          <>
            <Box className="slot-details-header">
              <Icon className="slot-details-header-icon" fontSize="inherit">
                schedule
              </Icon>
              <Box className="slot-details-header-infos">
                <Typography
                  className="slot-details-header-label"
                  variant="overline"
                >
                  Agendamento para:
                </Typography>
                <Typography
                  className="slot-details-header-text"
                  variant="body2"
                >
                  {`${moment(date).utc().startOf('d').format('DD/MM/YYYY')} - ${
                    timeRange[0]
                  } às ${timeRange[1]}`}
                </Typography>
              </Box>
            </Box>
            <Typography className="list-professionals-title" variant="caption">
              Selecione o profissional com quem deseja agendar o horário:
            </Typography>
            <List>
              {professionalsSlotInfos.map(
                ({
                  professionalId,
                  meetingId,
                  vacanciesFilled,
                  vacanciesTotal,
                }) => (
                  <ListItem
                    key={professionalId}
                    button
                    onClick={
                      () => handleSelectProfessional(professionalId, meetingId)
                      // eslint-disable-next-line react/jsx-curly-newline
                    }
                    disabled={vacanciesFilled >= vacanciesTotal}
                  >
                    <ListItemAvatar>
                      <Avatar className="item-avatar-container">
                        <Icon>person</Icon>
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      className="item-text-container"
                      primary={listProfessionals[professionalId]?.name}
                      primaryTypographyProps={{
                        variant: 'body2',
                      }}
                      secondary={listProfessionals[professionalId]?.areaName}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: 'inherit',
                        className: 'item-text-area',
                      }}
                    />
                    <Tooltip
                      title={getTooltipVacancies(
                        vacanciesFilled,
                        vacanciesTotal,
                      )}
                    >
                      <Typography className="item-vacancies" variant="caption">
                        {`${vacanciesFilled}/${vacanciesTotal}`}
                      </Typography>
                    </Tooltip>
                    <Icon className="item-icon-go" color="inherit">
                      chevron_right
                    </Icon>
                  </ListItem>
                ),
              )}
            </List>
          </>
        )}
      </DialogContent>
      {!loading && (
        <DialogActions>
          <Button onClick={handleCloseModal}>Fechar</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default memo(SlotProfessionalsModal);
